import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PdsBox, PdsCard, PdsMantineText, PdsMantineButton } from '@podium-design-system/react-components';
import {
  WORKFLOW_FLAG_ORDER,
  outcomeByKind,
  isWorkflowStepComplete,
} from '../utils/investigationWorkflowMask';

const WORKFLOW_FLAG_LABELS = {
  enableDomainIq: { label: 'DomainIQ', logShort: 'DomainIQ' },
  enableSecurityTrails: { label: 'SecurityTrails', logShort: 'SecurityTrails' },
  enableCrtSh: { label: 'Certificate Transparency (crt.sh)', logShort: 'crt.sh' },
  enableUrlScan: { label: 'URLScan (direct scan)', logShort: 'URLScan (scan)' },
  enableUrlScanSearch: { label: 'URLScan (search)', logShort: 'URLScan (search)' },
  enablePublicWww: { label: 'PublicWWW', logShort: 'PublicWWW' },
};

const WORKFLOW_STEPS = WORKFLOW_FLAG_ORDER.map((step) => ({
  ...step,
  ...WORKFLOW_FLAG_LABELS[step.flag],
}));

function buildToolRows(flags, outcomes) {
  if (!flags || typeof flags !== 'object') return [];
  const enabled = WORKFLOW_STEPS.filter((s) => flags[s.flag]);
  if (enabled.length === 0) return [];
  let seenRunning = false;
  return enabled.map((step) => {
    const done = isWorkflowStepComplete(step, flags, outcomes);
    let state;
    if (done) {
      state = 'done';
    } else if (!seenRunning) {
      state = 'running';
      seenRunning = true;
    } else {
      state = 'waiting';
    }
    return { ...step, state };
  });
}

/** Dashboard-style console lines (same tone as preview job progress). */
function buildWorkflowLogLines(toolRows, outcomes) {
  if (!toolRows.length) return [];
  return toolRows.map((row) => {
    const short = row.logShort;
    const o = row.outcomeKind ? outcomeByKind(outcomes, row.outcomeKind) : null;
    if (row.state === 'done') {
      if (o?.message) return `${short}: ${o.message}`;
      return `${short}: completed`;
    }
    if (row.state === 'running') return `${short}: retrieving…`;
    return `${short}: queued…`;
  });
}

function fallbackLogLines() {
  return ['Workflow: starting up…'];
}

/**
 * Full-screen centered HUD while a case investigation workflow is busy.
 * Renders via portal so fixed positioning is not clipped by page layout.
 */
export default function CaseWorkflowMaskOverlay({
  awaitingCaseIngest,
  investigationsEmpty,
  targetValue,
  detailSnapshot,
  /** Client-side flags from Investigate modal until GET /details persists workflowRequestedTools */
  workflowToolsOverride = null,
  /** Dashboard home: full-screen HUD driven by preview job `progress` lines instead of investigation /details */
  homePreview = false,
  previewProgressLines = null,
  /** Optional line(s) under the title (e.g. SecurityTrails status during keyword/IP search) */
  homePreviewSecondaryMeta = null,
  /** Short hint under the log (streaming / multi-source guidance) */
  homePreviewFooter = null,
  /** Case page: bypass overlay when workflow appears stuck */
  onDismiss = null,
}) {
  const showIngestCopy = awaitingCaseIngest && investigationsEmpty;
  const showHomePreview = Boolean(homePreview) && !showIngestCopy;
  const flagsForRows =
    !showIngestCopy && !showHomePreview
      ? detailSnapshot?.workflowRequestedTools ?? workflowToolsOverride ?? null
      : null;
  const toolRows = flagsForRows ? buildToolRows(flagsForRows, detailSnapshot?.toolOutcomes) : [];
  const serverWorkflowProgress =
    !showIngestCopy &&
    !showHomePreview &&
    Array.isArray(detailSnapshot?.workflowProgress) &&
    detailSnapshot.workflowProgress.length > 0;

  const logLines = showHomePreview
    ? Array.isArray(previewProgressLines) && previewProgressLines.length > 0
      ? previewProgressLines
      : fallbackLogLines()
    : serverWorkflowProgress
      ? detailSnapshot.workflowProgress
      : toolRows.length
        ? buildWorkflowLogLines(toolRows, detailSnapshot?.toolOutcomes)
        : fallbackLogLines();

  const runningRow = toolRows.find((r) => r.state === 'running');
  const nextWaitingRow = toolRows.find((r) => r.state === 'waiting');
  const doneShortLabels = toolRows.filter((r) => r.state === 'done').map((r) => r.logShort);

  const [elapsedMs, setElapsedMs] = useState(0);
  const startRef = useRef(null);
  const [canDismiss, setCanDismiss] = useState(false);

  useEffect(() => {
    startRef.current = performance.now();
    setCanDismiss(false);
    const dismissTimer = onDismiss ? window.setTimeout(() => setCanDismiss(true), 90_000) : undefined;
    const id = setInterval(() => {
      if (startRef.current != null) {
        setElapsedMs(Math.floor(performance.now() - startRef.current));
      }
    }, 50);
    return () => {
      clearInterval(id);
      if (dismissTimer) window.clearTimeout(dismissTimer);
    };
  }, [showIngestCopy, showHomePreview, targetValue, onDismiss]);

  const shell = (
    <PdsBox
      role="dialog"
      aria-modal="true"
      aria-busy="true"
      aria-live="polite"
      aria-label={
        showIngestCopy
          ? 'Node data is still being saved'
          : showHomePreview
            ? 'Home preview in progress'
            : 'Node data is still loading'
      }
      direction="column"
      alignItems="center"
      justifyContent="center"
      padding="xl"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 20050,
        background: 'rgba(255, 255, 255, 0.94)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
      }}
    >
      <PdsCard
        border
        borderRadius="md"
        padding="md"
        color="neutral"
        contrast="min"
        className="dashboard-preview-hud"
        style={{ width: '100%', maxWidth: 520 }}
      >
        <PdsBox direction="column" gap="sm">
          {showIngestCopy ? (
            <>
              <PdsBox direction="row" alignItems="center" gap="xs" style={{ flexWrap: 'wrap' }}>
                <span className="argus-type-page-heading">Saving case</span>
              </PdsBox>
              <div className="dashboard-preview-bar-track" aria-hidden="true">
                <div className="dashboard-preview-bar-fill" />
              </div>
              <PdsMantineText type="textline" component="div" className="dashboard-preview-elapsed">
                Elapsed {String(elapsedMs).padStart(8, ' ')} ms
              </PdsMantineText>
              <PdsMantineText type="textline" color="neutral" component="div" className="argus-type-meta">
                Collecting and storing node data for this case. This can take several minutes.
              </PdsMantineText>
              <PdsBox direction="column" gap="xs" className="dashboard-preview-console">
                <PdsMantineText type="textline" component="div" className="dashboard-preview-console-line">
                  Case ingest: in progress…
                </PdsMantineText>
                <PdsMantineText type="textline" component="div" className="dashboard-preview-console-line">
                  You can return from the case list — work continues in the background.
                </PdsMantineText>
              </PdsBox>
              <PdsMantineText type="textline" color="neutral" component="div" className="argus-type-meta">
                Case details and the node map will appear when ingest finishes.
              </PdsMantineText>
              {canDismiss && onDismiss && (
                <PdsMantineButton
                  type="button"
                  size="sm"
                  rank="outline"
                  color="neutral"
                  onClick={onDismiss}
                  style={{ alignSelf: 'flex-start', marginTop: 4 }}
                >
                  View case anyway
                </PdsMantineButton>
              )}
            </>
          ) : showHomePreview ? (
            <>
              <PdsBox direction="row" alignItems="center" gap="xs" style={{ flexWrap: 'wrap' }}>
                <span className="argus-type-page-heading">Preview — </span>
                <span className="argus-data">{targetValue ?? '…'}</span>
              </PdsBox>
              {homePreviewSecondaryMeta ? (
                <PdsMantineText type="textline" color="neutral" component="div" className="argus-type-meta">
                  {homePreviewSecondaryMeta}
                </PdsMantineText>
              ) : null}
              <div className="dashboard-preview-bar-track" aria-hidden="true">
                <div className="dashboard-preview-bar-fill" />
              </div>
              <PdsMantineText type="textline" component="div" className="dashboard-preview-elapsed">
                Elapsed {String(elapsedMs).padStart(8, ' ')} ms
              </PdsMantineText>
              <PdsMantineText type="textline" color="neutral" component="div" className="argus-type-meta">
                Large targets can take several minutes — the log below updates as each step reports.
              </PdsMantineText>
              <PdsBox direction="column" gap="xs" className="dashboard-preview-console">
                {logLines.map((line, i) => (
                  <PdsMantineText
                    key={`home-preview-${i}`}
                    type="textline"
                    component="div"
                    className="dashboard-preview-console-line"
                  >
                    {line}
                  </PdsMantineText>
                ))}
              </PdsBox>
              {homePreviewFooter ? (
                <PdsMantineText type="textline" color="neutral" component="div" className="argus-type-meta">
                  {homePreviewFooter}
                </PdsMantineText>
              ) : (
                <PdsMantineText type="textline" color="neutral" component="div" className="argus-type-meta">
                  Results stream into the preview below as each source returns.
                </PdsMantineText>
              )}
            </>
          ) : (
            <>
              <PdsBox direction="row" alignItems="center" gap="xs" className="investigation-details-heading" style={{ flexWrap: 'wrap' }}>
                <span className="investigation-details-heading-inner">
                  <span className="argus-type-page-heading">Node — </span>
                  <span className="argus-data argus-data-focal investigation-details-heading-target">{targetValue ?? '…'}</span>
                </span>
              </PdsBox>
              <div className="dashboard-preview-bar-track" aria-hidden="true">
                <div className="dashboard-preview-bar-fill" />
              </div>
              <PdsMantineText type="textline" component="div" className="dashboard-preview-elapsed">
                Elapsed {String(elapsedMs).padStart(8, ' ')} ms
              </PdsMantineText>
              {toolRows.length === 0 && (
                <PdsMantineText type="textline" color="neutral" component="div" className="argus-type-meta">
                  Results are being gathered — stay on this page and tabs will populate as each tool completes.
                </PdsMantineText>
              )}
              {doneShortLabels.length > 0 && !serverWorkflowProgress && (
                <PdsMantineText type="textline" color="neutral" component="div" className="argus-type-meta">
                  Completed: {doneShortLabels.join(', ')}
                </PdsMantineText>
              )}
              {runningRow && !serverWorkflowProgress && (
                <PdsMantineText type="textline" color="neutral" component="div" className="argus-type-meta">
                  {runningRow.logShort}: retrieving…
                </PdsMantineText>
              )}
              {nextWaitingRow && runningRow && !serverWorkflowProgress && (
                <PdsMantineText type="textline" color="neutral" component="div" className="argus-type-meta">
                  {nextWaitingRow.logShort}: queued…
                </PdsMantineText>
              )}
              {toolRows.length > 0 && (
                <PdsMantineText type="textline" color="neutral" component="div" className="argus-type-meta">
                  Large targets can take several minutes — the log below updates as each step reports.
                </PdsMantineText>
              )}
              <PdsBox direction="column" gap="xs" className="dashboard-preview-console">
                {logLines.map((line, i) => (
                  <PdsMantineText
                    key={`wf-line-${i}`}
                    type="textline"
                    component="div"
                    className="dashboard-preview-console-line"
                  >
                    {line}
                  </PdsMantineText>
                ))}
              </PdsBox>
              <PdsMantineText type="textline" color="neutral" component="div" className="argus-type-meta">
                Results will appear in the tabs after each enabled tool has finished.
              </PdsMantineText>
              {canDismiss && onDismiss && (
                <PdsMantineButton
                  type="button"
                  size="sm"
                  rank="outline"
                  color="neutral"
                  onClick={onDismiss}
                  style={{ alignSelf: 'flex-start', marginTop: 4 }}
                >
                  View case anyway
                </PdsMantineButton>
              )}
            </>
          )}
        </PdsBox>
      </PdsCard>
    </PdsBox>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(shell, document.body);
}
